/* =========================================================
   AF — Review Dump Masonry Gallery (cached JS)
   File: assets/af-review-dump-gallery.js

   Same logic:
   - If csv_url exists: fetch CSV, parse first cell per row, skip header
   - Else: use server-rendered textarea images
   - Optional lightbox

   Optimizations:
   - Per-section init guard + AbortController to prevent duplicate listeners
   - Delegated click handler for lightbox
   - Uses documentElement overflow lock only while open
   ========================================================= */
(() => {
  "use strict";

  const looksLikeUrl = (s) => /^https?:\/\/.+/i.test(s);

  const cell = (s) => (s || "").trim().replace(/^"|"$/g, "");
  const posInt = (s) => {
    const n = parseInt(cell(s), 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const parseCsvText = (text) => {
    const lines = text
      .replace(/\r/g, "")
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);

    // Each returned item is { url, w, h }. Optional 2nd/3rd columns carry the
    // image's real pixel width/height; when present they let us reserve the
    // exact box before the image loads, so the masonry never shifts on load.
    // Missing dims → w/h are 0 and the item behaves exactly as before.
    const items = [];
    for (const line of lines) {
      const parts = line.split(",");
      const url = cell(parts[0]);
      if (!url) continue;

      // Skip header if first row isn't a URL
      if (items.length === 0 && !looksLikeUrl(url)) continue;

      if (looksLikeUrl(url)) items.push({ url, w: posInt(parts[1]), h: posInt(parts[2]) });
    }
    return items;
  };

  // ---------- Responsive images ----------
  // These are TEXT screenshots, so candidates run a touch large and cap near the
  // Shopify source width (864). Stepping up keeps small text legible after the
  // browser downscales; going too small makes it mushy.
  const SRCSET_WIDTHS = [206, 275, 413, 550, 688, 825];
  // sizes mirrors the CSS column-count breakpoints (989 / 749): desktop ~5 cols
  // across the full-width wall (~20vw), tablet 3 (~33vw), mobile 2 (~48vw).
  const IMG_SIZES = "(min-width: 990px) 20vw, (min-width: 750px) 33vw, 48vw";
  const DEFAULT_SRC_W = 550;
  const isShopifyCdn = (u) => {
    try {
      return /(^|\.)cdn\.shopify\.com$/i.test(new URL(u, location.href).hostname);
    } catch (e) {
      return false;
    }
  };
  const cdnW = (url, w) =>
    isShopifyCdn(url) ? url + (url.indexOf("?") >= 0 ? "&" : "?") + "width=" + w : url;
  const buildSrcset = (url) =>
    isShopifyCdn(url) ? SRCSET_WIDTHS.map((w) => cdnW(url, w) + " " + w + "w").join(", ") : "";

  // CLS note: the wall's height is reserved at FIRST PAINT by an inline
  // min-height on [data-af-rd-masonry] in the Liquid (only when a JS-filled
  // source — JSON/CSV — is configured), so the footer starts below the fold and
  // the async image injection below doesn't move visible content. Reserving here
  // in JS would be too late (this runs after the fetch, post first paint). So on
  // the empty/error paths we CLEAR that reserve; on success the injected images
  // (each carrying width/height) define the height.
  const renderUrls = (grid, empty, items) => {
    grid.innerHTML = "";
    if (!items || !items.length) {
      grid.removeAttribute("style"); // drop the first-paint reserve so :empty hides the grid
      empty.hidden = false;
      return;
    }
    empty.hidden = true;
    grid.style.minHeight = "0px"; // images (with width/height) now define the height; keep display:block

    const frag = document.createDocumentFragment();
    let n = grid.children.length;
    for (const it of items) {
      const fig = document.createElement("figure");
      fig.className = "af-rd__item";
      // a11y: each screenshot is a keyboard-reachable button that opens the lightbox
      fig.tabIndex = 0;
      fig.setAttribute("role", "button");
      fig.setAttribute("aria-label", "View customer review screenshot " + ++n);
      const img = document.createElement("img");
      const srcset = buildSrcset(it.url);
      if (srcset) {
        img.srcset = srcset;
        img.sizes = IMG_SIZES;
        img.src = cdnW(it.url, DEFAULT_SRC_W);
      } else {
        img.src = it.url;
      }
      // Full-resolution source for the lightbox — the grid shows a small
      // (width=413) thumbnail, so the popup must load the original or the
      // enlarged screenshot text would be unreadable.
      img.dataset.full = it.url;
      img.alt = "";
      img.loading = "lazy";
      img.decoding = "async";
      img.draggable = false;
      // With width:100% / height:auto CSS, the width+height attributes give the
      // browser the aspect ratio so it reserves the correct height before load
      // (no shift). Omitted when the CSV has no dims — same as the old behaviour.
      if (it.w && it.h) {
        img.width = it.w;
        img.height = it.h;
      }
      fig.appendChild(img);
      frag.appendChild(fig);
    }
    grid.appendChild(frag);
  };

  const initOne = (root) => {
    if (!root) return;
    if (root.dataset.afRdInit === "1") return;
    root.dataset.afRdInit = "1";

    const grid = root.querySelector("[data-af-rd-masonry]");
    const empty = root.querySelector("[data-af-rd-empty]");
    if (!grid || !empty) return;

    const jsonUrl = (root.dataset.jsonUrl || "").trim();
    const csvUrl = (root.dataset.csvUrl || "").trim();
    const lightboxEnabled = root.dataset.lightbox === "true";

    // AbortController for all listeners per instance (prevents stacking on section reload)
    const ctrl = new AbortController();
    root.__afRdAbort = ctrl;

    // ---------- Off-screen skip (content-visibility) ----------
    // An item is armed (.is-cv) only once its image has loaded, with its exact
    // rendered size pinned as the placeholder, so a skipped item keeps the same
    // box — the CSS-column balance never shifts — and items still waiting on a
    // lazy image are never skipped. A width change (rotation) re-measures.
    if (window.CSS && CSS.supports("content-visibility", "auto") &&
        CSS.supports("contain-intrinsic-size", "1px 1px") && window.ResizeObserver) {
      const toArm = new Set();
      let armRaf = 0;
      const armFlush = () => {
        armRaf = 0;
        const sizes = []; // read every size first, then write — one layout
        toArm.forEach((fig) => {
          const img = fig.querySelector("img");
          if (!fig.isConnected || !img) return;
          const r = img.getBoundingClientRect();
          if (r.width > 0 && r.height > 0) sizes.push([fig, r.width, r.height]);
        });
        toArm.clear();
        for (const [fig, w, h] of sizes) {
          fig.style.containIntrinsicSize = w + "px " + h + "px";
          fig.classList.add("is-cv");
        }
      };
      const arm = (fig) => {
        toArm.add(fig);
        if (!armRaf) armRaf = requestAnimationFrame(armFlush);
      };

      // load doesn't bubble — catch every grid image's load in the capture phase
      grid.addEventListener(
        "load",
        (e) => {
          const fig = e.target.tagName === "IMG" && e.target.closest(".af-rd__item");
          if (fig) arm(fig);
        },
        { capture: true, signal: ctrl.signal }
      );

      let lastW = 0;
      const ro = new ResizeObserver((entries) => {
        const w = entries[0].contentRect.width;
        if (lastW && w !== lastW) {
          grid.querySelectorAll(".af-rd__item.is-cv").forEach((fig) => {
            fig.classList.remove("is-cv");
            fig.style.containIntrinsicSize = "";
            arm(fig);
          });
        }
        lastW = w;
      });
      ro.observe(grid);
      ctrl.signal.addEventListener("abort", () => ro.disconnect());
    }

    // ---------- Lightbox ----------
    let lb, lbImg, lbClose;

    const mountLightbox = () => {
      if (!lightboxEnabled) return;

      lb = root.querySelector(".af-rd__lb");
      lbImg = root.querySelector(".af-rd__lbImg");
      lbClose = root.querySelector(".af-rd__lbClose");
      if (!lb || !lbImg || !lbClose) return;

      // Signal that thumbnails are now click-to-zoom (drives the cursor).
      grid.classList.add("af-rd__masonry--zoom");

      let lbLastFocused = null;
      let hideTimer = 0;
      const prefersReduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      const open = (src) => {
        if (hideTimer) { window.clearTimeout(hideTimer); hideTimer = 0; }
        lbLastFocused = document.activeElement;
        // Rise-and-fade the image in once the full-res file has loaded.
        lbImg.classList.remove("is-loaded");
        lbImg.onload = () => lbImg.classList.add("is-loaded");
        lbImg.src = src;
        if (lbImg.complete && lbImg.naturalWidth) lbImg.classList.add("is-loaded"); // cached
        lb.hidden = false;
        // Lock scroll WITHOUT a layout shift. Plain overflow:hidden removes the
        // scrollbar, and the page + full-width sticky header reflow into the
        // freed width — a small jump seen behind the fading overlay. Reserve the
        // scrollbar's width as padding so nothing moves. (0 on overlay-scrollbar
        // / mobile, so no-op there.)
        const de = document.documentElement;
        const sbw = window.innerWidth - de.clientWidth;
        de.style.overflow = "hidden";
        if (sbw > 0) de.style.paddingRight = sbw + "px";
        // Trigger the fade-in on the next frame — the transition needs a
        // rendered opacity:0 start state (skipped straight from display:none).
        requestAnimationFrame(() => lb.classList.add("is-open"));
        // Move focus into the dialog and trap it (Escape is already handled below).
        if (typeof trapFocus === "function") {
          trapFocus(lb, lbClose);
        } else if (lbClose) {
          lbClose.focus();
        }
      };

      const close = () => {
        lb.classList.remove("is-open"); // fade out
        document.documentElement.style.overflow = "";
        document.documentElement.style.paddingRight = ""; // release the scrollbar reserve
        if (hideTimer) window.clearTimeout(hideTimer);
        hideTimer = window.setTimeout(() => {
          lb.hidden = true;
          lbImg.src = "";
          lbImg.classList.remove("is-loaded");
          hideTimer = 0;
        }, prefersReduced() ? 0 : 240);
        // Return focus to the thumbnail (or wherever it was) that opened the lightbox.
        if (typeof removeTrapFocus === "function") {
          removeTrapFocus(lbLastFocused);
        } else if (lbLastFocused && lbLastFocused.focus) {
          lbLastFocused.focus();
        }
      };

      // delegated click (one handler)
      grid.addEventListener(
        "click",
        (e) => {
          const img = e.target.closest("img");
          if (!img) return;
          e.preventDefault();
          open(img.dataset.full || img.currentSrc || img.src);
        },
        { signal: ctrl.signal }
      );

      grid.addEventListener(
        "keydown",
        (e) => {
          if (e.key !== "Enter" && e.key !== " ") return;
          const img = e.target.closest(".af-rd__item")?.querySelector("img");
          if (!img) return;
          e.preventDefault();
          open(img.dataset.full || img.currentSrc || img.src);
        },
        { signal: ctrl.signal }
      );

      lb.addEventListener(
        "click",
        (e) => {
          if (e.target === lb) close();
        },
        { signal: ctrl.signal }
      );

      lbClose.addEventListener("click", close, { signal: ctrl.signal });

      window.addEventListener(
        "keydown",
        (e) => {
          if (!lb || lb.hidden) return;
          if (e.key === "Escape") close();
        },
        { signal: ctrl.signal }
      );
    };

    // Priority 1: App-Proxy JSON endpoint (stable URL, served by the app on
    // Refresh). Same-origin, shape [{url,width,height}, ...]; width/height let
    // us reserve each image's box so the masonry never shifts on load.
    if (jsonUrl) {
      fetch(jsonUrl, { headers: { Accept: "application/json" } })
        .then((r) => {
          if (!r.ok) throw new Error("JSON fetch failed: " + r.status);
          return r.json();
        })
        .then((data) => {
          const items = (Array.isArray(data) ? data : [])
            .map((o) => ({
              url: o && o.url ? String(o.url).trim() : "",
              w: parseInt(o && o.width, 10) || 0,
              h: parseInt(o && o.height, 10) || 0,
            }))
            .filter((it) => looksLikeUrl(it.url));
          renderUrls(grid, empty, items);
          mountLightbox();
        })
        .catch((err) => {
          console.error("[AF Review Dump] JSON error:", err);
          grid.removeAttribute("style"); // drop the first-paint reserve on failure
          empty.hidden = false;
        });
      return;
    }

    // Priority 2: no CSV → use existing liquid-rendered items.
    if (!csvUrl) {
      const hasItems = !!grid.querySelector("img");
      empty.hidden = hasItems;
      mountLightbox();
      return;
    }

    // Priority 3: fetch CSV + render
    fetch(csvUrl, { cache: "force-cache" })
      .then((r) => {
        if (!r.ok) throw new Error("CSV fetch failed: " + r.status);
        return r.text();
      })
      .then((text) => {
        const urls = parseCsvText(text);
        renderUrls(grid, empty, urls);
        mountLightbox();
      })
      .catch((err) => {
        console.error("[AF Review Dump] CSV error:", err);
        grid.removeAttribute("style"); // drop the first-paint reserve on failure
        empty.hidden = false;
      });
  };

  const initAll = () => {
    document.querySelectorAll("[data-af-rd]").forEach((root) => {
      // If this section was re-rendered, abort old listeners
      if (root.__afRdAbort) {
        try { root.__afRdAbort.abort(); } catch (e) {}
        root.__afRdAbort = null;
        root.dataset.afRdInit = "";
      }
      initOne(root);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }

  // Theme editor / section reload safety
  document.addEventListener("shopify:section:load", initAll);
})();
